import { useEffect, useState } from "react";
import { toast } from "sonner";
import DashboardShell from "@/components/DashboardShell";
import { api, apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Check, X, ShieldCheck } from "lucide-react";

const badge = (s) => ({
  pending: "bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-amber-400",
  approved: "bg-green-100 text-green-700 dark:bg-green-500/15 dark:text-green-400",
  rejected: "bg-muted text-muted-foreground",
}[s] || "bg-muted text-muted-foreground");

const RequestRow = ({ r, i, act }) => (
  <div className={`flex items-center gap-4 px-5 py-4 ${i > 0 ? "border-t border-border" : ""}`} data-testid={`pr-${r.id}`}>
    <div className="min-w-0 flex-1">
      <div className="font-medium text-foreground truncate">{r.user_name} <span className="text-muted-foreground font-normal">· {r.user_email}</span></div>
      <div className="text-xs text-muted-foreground">Ref: {r.reference || "—"} · {new Date(r.created_at).toLocaleString()}</div>
    </div>
    <div className="text-right shrink-0">
      <div className="font-display font-semibold text-foreground capitalize">{r.tier} · {r.amount_inr ? `₹${Number(r.amount_inr).toLocaleString("en-IN")}` : `€${r.amount}`}</div>
      <span className={`text-[11px] px-2 py-0.5 rounded-full ${badge(r.status)}`}>{r.status}</span>
    </div>
    {r.status === "pending" && (
      <div className="flex items-center gap-2 shrink-0">
        <Button size="sm" onClick={() => act(r.id, "approve")} className="rounded-full" data-testid={`approve-${r.id}`}><Check size={14} className="mr-1" /> Approve</Button>
        <Button size="sm" variant="outline" onClick={() => act(r.id, "reject")} className="rounded-full" data-testid={`reject-${r.id}`}><X size={14} /></Button>
      </div>
    )}
  </div>
);

export default function Admin() {
  const { user } = useAuth();
  const [items, setItems] = useState(null);

  const load = async () => {
    try { const { data } = await api.get("/admin/payment-requests"); setItems(data); }
    catch (e) { toast.error(apiErr(e)); setItems([]); }
  };
  useEffect(() => { load(); }, []);

  const act = async (id, action) => {
    try {
      await api.post(`/admin/payment-requests/${id}/${action}`);
      toast.success(action === "approve" ? "Plan activated" : "Request rejected");
      load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  if (user && user.role !== "admin") return (
    <DashboardShell><div className="rounded-2xl border border-dashed border-border p-16 text-center text-muted-foreground">Admins only.</div></DashboardShell>
  );

  const all = items || [];
  const pending = all.filter((r) => r.status === "pending");
  const history = all.filter((r) => r.status !== "pending");

  const List = ({ rows, empty, testid }) => (
    rows.length === 0 ? (
      <div className="mt-6 rounded-2xl border border-dashed border-border p-16 text-center text-muted-foreground">{empty}</div>
    ) : (
      <div className="mt-6 rounded-2xl border border-border bg-card overflow-hidden" data-testid={testid}>
        {rows.map((r, i) => <RequestRow key={r.id} r={r} i={i} act={act} />)}
      </div>
    )
  );

  return (
    <DashboardShell>
      <div className="flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-primary mb-3"><ShieldCheck size={14} /> Admin</div>
      <h1 className="font-display text-4xl font-semibold tracking-tight text-foreground">Payment requests</h1>
      <p className="mt-2 text-muted-foreground">Review UPI payments and activate plans. {pending.length} pending.</p>

      {items === null ? null : (
        <Tabs defaultValue="pending" className="mt-8">
          <TabsList data-testid="admin-tabs">
            <TabsTrigger value="pending" data-testid="tab-pending">Pending {pending.length > 0 && <span className="ml-1.5 text-[11px] px-1.5 py-0.5 rounded-full bg-amber-500/20 text-amber-600 dark:text-amber-400">{pending.length}</span>}</TabsTrigger>
            <TabsTrigger value="history" data-testid="tab-history">History {history.length > 0 && <span className="ml-1.5 text-muted-foreground">({history.length})</span>}</TabsTrigger>
          </TabsList>
          <TabsContent value="pending"><List rows={pending} empty="No pending requests. You're all caught up." testid="payment-requests" /></TabsContent>
          <TabsContent value="history"><List rows={history} empty="No approved or rejected requests yet." testid="payment-history" /></TabsContent>
        </Tabs>
      )}
    </DashboardShell>
  );
}
